import PropTypes from 'prop-types';
import React, { memo, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import SignupAddFundsForm, { useShouldShowAddFundsForm } from '@bingads-webui-cc-react/signup-add-funds';

import { CountryInput } from './inputs/country-input';
import { CouponInput } from './inputs/coupon-input';
import { PaymentTypeInput } from './inputs/payment-type-input';
import { PaymentInstrumentSelector } from './inputs/payment-instrument-input';
import { addressPropType, i18nPropType, PropTypeBaseAddPIForm, PropTypeExistingPis, PropTypeTopUpLimit } from './shapes';

const AddPIFormUnwrapped = ({
  aid,
  accountNumber,
  cid,
  address,
  country,
  currency,
  countryOptions,
  currencyMapping,
  fetchOfflinePiInstructions,
  i18n,
  initialCouponCode,
  paymentAdderOptions,
  paymentType,
  paymentTypeOptions,
  paymentTypeDisabled,
  setCountry,
  setCouponCode,
  setIsValid,
  setOfflinePiAddress,
  setPaymentType,
  existingPis,
  piid,
  piType,
  setPiid,
  setPiType,
  EstimatedTaxAmount,
  TopUpLimits,
  SignupAddFundsResult,
  isAutoRechargeOn,
  setIsAutoRechargeOn,
  paymentAmount,
  setPaymentAmount,
  showCouponCode,
  showPaymentTypeOptions,
  showPiTypeOptions,
  countryInputHelpPopUpId,
  isSmartAddFundsUrl,
  alwaysShowAddFundsAmountError,
  permissions,
  pidlAdderClassName,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
}) => {
  const {
    isSmartAddFundsEnabled,
    setLoading,
    restfulService,
    ccBaseUrl,
  } = paymentAdderOptions;

  const [couponIsValid, setCouponIsValid] = useState(true);
  const [piFormIsValid, setPiFormIsValid] = useState(false);
  const [addFundsIsValid, setAddFundsIsValid] = useState(false);
  const showAddFundsForm = useShouldShowAddFundsForm(paymentType, piType, country, isSmartAddFundsEnabled);

  useLayoutEffect(
    () => {
      if (!showAddFundsForm) {
        setPaymentAmount(null);
        setIsAutoRechargeOn(undefined);
      }
    },
    [showAddFundsForm, setIsAutoRechargeOn, setPaymentAmount]
  );

  useLayoutEffect(
    () => {
      if (showAddFundsForm && _.isUndefined(isAutoRechargeOn)) {
        setIsAutoRechargeOn(true);
      }
    },
    [isAutoRechargeOn, permissions, setIsAutoRechargeOn, showAddFundsForm]
  );

  const controlledCountries = useMemo(
    () => _.chain(currencyMapping)
      .filter(curr => _.isNumber(curr.ControlledCurrencyCountryId))
      .map(curr => _.findWhere(countryOptions, { Id: curr.ControlledCurrencyCountryId }).Code)
      .value(),
    [countryOptions, currencyMapping]
  );

  const nonControlledCountries = useMemo(
    () => _.filter(countryOptions, c => !_.contains(controlledCountries, c.Code)),
    [controlledCountries, countryOptions]
  );

  const filteredCountryOptions = useMemo(
    () => {
      const currencyDetails = currencyMapping[currency];
      const controlledCountryId = currencyDetails && currencyDetails.ControlledCurrencyCountryId;

      return controlledCountryId
        ? _.filter(countryOptions, c => c.Id === controlledCountryId)
        : nonControlledCountries;
    },
    [countryOptions, currency, currencyMapping, nonControlledCountries]
  );

  // if currency switches to/from a controlled currency, update the list
  useLayoutEffect(
    () => {
      if (!_.findWhere(filteredCountryOptions, { Code: country })) {
        setCountry(_.first(filteredCountryOptions).Code);
      }
    },
    [country, setCountry, filteredCountryOptions]
  );

  const currencyId = useMemo(
    () => {
      const currencyDetails = currencyMapping[currency.toUpperCase()];
      return currencyDetails && currencyDetails.Id;
    },
    [currency, currencyMapping]
  );

  useLayoutEffect(
    () => {
      setIsValid(couponIsValid && piFormIsValid && (!showAddFundsForm || addFundsIsValid));
    },
    [addFundsIsValid, couponIsValid, piFormIsValid, setIsValid, showAddFundsForm]
  );

  return (
    <div>
      {showCouponCode &&
        <div className="coupon-input" >
          <CouponInput
            i18n={i18n}
            initialCouponCode={initialCouponCode}
            setCouponCode={setCouponCode}
            setLoading={setLoading}
            setIsValid={setCouponIsValid}
            restfulService={restfulService}
            ccBaseUrl={ccBaseUrl}
            cid={cid}
            country={country}
            currencyId={currencyId}
          />
        </div>
      }
      <div className="country-input" >
        <CountryInput
          i18n={i18n}
          countryOptions={filteredCountryOptions}
          country={country}
          setCountry={setCountry}
          setPiid={setPiid}
          helpPopUpId={countryInputHelpPopUpId}
        />
      </div>
      {showPaymentTypeOptions &&
        <div className="payment-type-input" >
          <PaymentTypeInput
            i18n={i18n}
            paymentTypeOptions={paymentTypeOptions}
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            disabled={paymentTypeDisabled}
          />
        </div>
      }
      <div className="pi-type-input" >
        <PaymentInstrumentSelector
          cid={cid}
          pidlAdderClassName={pidlAdderClassName}
          paymentAdderOptions={paymentAdderOptions}
          i18n={i18n}
          address={address}
          offlinePiCountryList={filteredCountryOptions}
          country={country}
          paymentType={paymentType}
          currency={currency}
          setIsValid={setPiFormIsValid}
          setOfflinePiAddress={setOfflinePiAddress}
          existingPis={existingPis}
          piid={piid}
          piType={piType}
          setPiid={setPiid}
          setPiType={setPiType}
          setCountry={setCountry}
          showPiTypeOptions={showPiTypeOptions}
          shouldRetryWithPopup={shouldRetryWithPopup}
          shouldAuthenticateWithPopup={shouldAuthenticateWithPopup}
          permissions={permissions}
        />
      </div>
      {showAddFundsForm && (
        <SignupAddFundsForm
          aid={aid}
          cid={cid}
          fetchOfflinePiInstructions={fetchOfflinePiInstructions}
          accountNumber={accountNumber}
          currency={currency}
          EstimatedTaxAmount={EstimatedTaxAmount}
          piType={piType}
          piCountry={country}
          TopUpLimits={TopUpLimits}
          SignupAddFundsResult={SignupAddFundsResult}
          setIsValid={setAddFundsIsValid}
          paymentProps={paymentAdderOptions}
          isAutoRechargeOn={isAutoRechargeOn}
          setIsAutoRechargeOn={setIsAutoRechargeOn}
          paymentAmount={paymentAmount}
          setPaymentAmount={setPaymentAmount}
          isSmartAddFundsUrl={isSmartAddFundsUrl}
          alwaysShowAddFundsAmountError={alwaysShowAddFundsAmountError}
          permissions={permissions}
        />
      )}
    </div>
  );
};

AddPIFormUnwrapped.propTypes = {
  aid: PropTypes.number,
  accountNumber: PropTypes.string,
  cid: PropTypes.number.isRequired,
  country: PropTypes.string.isRequired,
  currencyMapping: PropTypes.objectOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    ControlledCurrencyCountryId: PropTypes.number,
  }).isRequired).isRequired,
  fetchOfflinePiInstructions: PropTypes.func,
  paymentAdderOptions: PropTypeBaseAddPIForm.isRequired,
  i18n: i18nPropType.isRequired,
  initialCouponCode: PropTypes.string,
  address: addressPropType,
  countryOptions: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  currency: PropTypes.string.isRequired,
  paymentType: PropTypes.number.isRequired,
  paymentTypeOptions: PropTypes.arrayOf(PropTypes.number),
  paymentTypeDisabled: PropTypes.bool,
  setCountry: PropTypes.func.isRequired,
  setCouponCode: PropTypes.func,
  setIsValid: PropTypes.func.isRequired,
  setOfflinePiAddress: PropTypes.func,
  setPaymentType: PropTypes.func,
  existingPis: PropTypeExistingPis,
  piid: PropTypes.number,
  piType: PropTypes.number.isRequired,
  setPiid: PropTypes.func.isRequired,
  setPiType: PropTypes.func,
  EstimatedTaxAmount: PropTypes.number,
  TopUpLimits: PropTypes.arrayOf(PropTypeTopUpLimit),
  SignupAddFundsResult: PropTypes.shape({
    submitting: PropTypes.bool,
    success: PropTypes.func,
    fail: PropTypes.func,
    cancel: PropTypes.func,
    CtpPiId: PropTypes.string,
    MtPiId: PropTypes.string,
  }),
  isAutoRechargeOn: PropTypes.bool,
  setIsAutoRechargeOn: PropTypes.func,
  paymentAmount: PropTypes.number,
  setPaymentAmount: PropTypes.func,
  showCouponCode: PropTypes.bool,
  showPaymentTypeOptions: PropTypes.bool,
  showPiTypeOptions: PropTypes.bool,
  countryInputHelpPopUpId: PropTypes.string,
  isSmartAddFundsUrl: PropTypes.bool,
  alwaysShowAddFundsAmountError: PropTypes.bool,
  permissions: PropTypes.shape({}),
  pidlAdderClassName: PropTypes.string,
  shouldRetryWithPopup: PropTypes.bool,
  shouldAuthenticateWithPopup: PropTypes.bool,
};

AddPIFormUnwrapped.defaultProps = {
  aid: null,
  accountNumber: null,
  address: {},
  paymentTypeOptions: [],
  fetchOfflinePiInstructions: _.noop,
  EstimatedTaxAmount: null,
  TopUpLimits: [],
  SignupAddFundsResult: null,
  isAutoRechargeOn: undefined,
  existingPis: [],
  piid: null,
  paymentAmount: null,
  initialCouponCode: null,
  setCouponCode: _.noop,
  setPaymentType: _.noop,
  setPiType: _.noop,
  setIsAutoRechargeOn: _.noop,
  setPaymentAmount: _.noop,
  setOfflinePiAddress: _.noop,
  showCouponCode: true,
  showPaymentTypeOptions: true,
  showPiTypeOptions: true,
  countryInputHelpPopUpId: 'Pop_BA_PaymentMethods_Index_AddCard_Country',
  isSmartAddFundsUrl: true,
  alwaysShowAddFundsAmountError: false,
  permissions: {},
  pidlAdderClassName: '',
  paymentTypeDisabled: false,
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
};

export const AddPIForm = memo(AddPIFormUnwrapped);
